import React, { useEffect, useRef, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { PHONE_INPUT_STYLES, KIRUSA_PHONE_STYLES } from './PhoneAuth';
import { getApiBaseUrl, formatPhoneLabel } from '../utils/chatIdentity';
import {
    DEFAULT_COUNTRY,
    getPhoneValidationError,
    normalizePhoneForOtp,
} from '../utils/phoneValidation';
import {
    getEmailAuthErrorMessage,
    getEmailValidationError,
    normalizeEmail,
} from '../utils/emailValidation';
import useOtpResendCooldown from '../hooks/useOtpResendCooldown';
import { getZipCodeError, normalizeZipCode } from '../utils/zipCode';
import { NAME_INVALID_MESSAGE, getProfileNameError, normalizeProfileName } from '../utils/profileName';

const OTP_LENGTH = 6;
// Same cap the Settings name editor and /auth/complete-profile enforce.
const PROFILE_FIELD_MAX_LENGTH = 120;

const labelClass = 'mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-black/45';
const inputClass = 'h-14 w-full rounded-2xl border border-black/10 bg-[#f9f9f9] px-4 text-base font-bold text-black outline-none transition placeholder:font-medium placeholder:text-gray-400 focus:border-[#D81F26] focus:bg-white focus:ring-4 focus:ring-[#D81F26]/15';
const primaryButtonClass = 'w-full rounded-full bg-black py-4 text-[12px] font-black uppercase tracking-[0.16em] text-white transition active:scale-[0.99] disabled:opacity-60 sm:py-3.5 sm:text-[11px]';
const errorBoxClass = 'rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600';
const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D81F26] focus-visible:ring-offset-2';

async function readJson(response) {
    try {
        return await response.json();
    } catch {
        return {};
    }
}

// The linking endpoints answer 401 for a wrong code too, so only these two details
// (require_session_identity in auth/router.py) mean the session itself is gone.
const SESSION_EXPIRED_DETAILS = ['Not authenticated', 'Session expired'];

function isSessionExpiredResponse(response, data) {
    return response.status === 401 && SESSION_EXPIRED_DETAILS.includes(data?.detail);
}

function sessionExpiredError() {
    const error = new Error('Session expired');
    error.sessionExpired = true;
    return error;
}

// The identifier the user signed in with: shown, already verified, not editable here
// (Settings owns changing it). Same box as the login inputs, just read-only.
const VerifiedContactField = ({ id, label, value }) => (
    <div>
        <label htmlFor={id} className={labelClass}>{label}</label>
        <div className="relative">
            <input
                id={id}
                type="text"
                readOnly
                tabIndex={-1}
                value={value}
                aria-describedby={`${id}-status`}
                className="h-[48px] w-full cursor-default truncate rounded-xl border border-border-control bg-surface-muted pl-4 pr-28 text-base font-bold text-fg outline-none"
            />
            <svg className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#414141]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-label="Verified">
                <path d="M20 6 9 17l-5-5" />
            </svg>
        </div>
    </div>
);

// Shown when the contact just verified belongs to another account — the same choice
// Settings offers, since both identities are proven at this point.
const MergePrompt = ({ preview, what, busy, onConfirm, onCancel }) => {
    const items = [
        ['vehicle', preview?.vehicles],
        ['saved memory', preview?.memories],
        ['document', preview?.documents],
        ['diagnostic ticket', preview?.tickets],
    ].filter(([, count]) => Number(count) > 0)
        .map(([label, count]) => `${count} ${label}${count === 1 ? '' : 's'}`);

    return (
        <div className="rounded-xl border border-border-control bg-surface-muted px-4 py-3.5">
            <p className="text-[14px] font-black text-fg">That {what} belongs to another account</p>
            <p className="mt-1 text-[13px] font-semibold leading-relaxed text-fg-muted">
                {items.length
                    ? `That account has ${items.join(', ')}. Combining moves everything into this account — nothing is deleted.`
                    : `That account is empty. Combining it will simply free up this ${what} for your account — nothing is deleted.`}
            </p>
            <div className="mt-3 flex gap-2.5 sm:gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={busy}
                    className={`min-h-[48px] flex-1 rounded-full border border-border-control bg-surface px-3 text-sm font-bold text-fg transition disabled:opacity-60 ${focusClass}`}
                >
                    Use a different {what}
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={busy}
                    className={`${primaryButtonClass} flex-1 !w-auto px-3 text-sm`}
                >
                    {busy ? 'Combining...' : 'Combine accounts'}
                </button>
            </div>
        </div>
    );
};

// Post-login "Tell us about you" step. Collects the name, the contact the user did NOT
// sign in with, and their region, so Settings opens fully filled in.
//
// The second contact is linked exactly the way Settings links it — /auth/add-email +
// /auth/verify-added-email, or /auth/request-otp + /auth/verify-added-phone — so it is
// only ever attached once its code has been proven.
// Mounted fresh on every open, so the form always starts from what the account has
// now — seeding it from an effect instead would show empty fields for a frame.
const ProfileSetupModal = ({ isOpen, ...props }) => (isOpen ? <ProfileSetupDialog {...props} /> : null);

const ProfileSetupDialog = ({ authState, onLogout, onComplete, onSessionExpired }) => {
    const authBaseUrl = `${getApiBaseUrl()}/auth`;
    const signedInPhone = String(authState?.phone || '').trim();
    const signedInEmail = String(authState?.email || '').trim();
    // Whichever identifier the account is missing is the one collected here.
    const needsEmail = !signedInEmail;
    const needsPhone = !needsEmail && !signedInPhone;
    const contactWhat = needsEmail ? 'email' : 'phone number';

    const [step, setStep] = useState('details'); // 'details' or 'verify'
    const [nameValue, setNameValue] = useState(() => authState?.profileName || '');
    // Only a valid ZIP is pre-filled; an older state/city value must be re-entered as a ZIP.
    const [regionValue, setRegionValue] = useState(() => normalizeZipCode(authState?.profileRegion));
    const [emailValue, setEmailValue] = useState('');
    const [phoneValue, setPhoneValue] = useState('');
    const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);
    const [pendingContact, setPendingContact] = useState('');
    const [otpValue, setOtpValue] = useState('');
    const [mergePreview, setMergePreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const {
        secondsRemaining: resendSeconds,
        isCoolingDown: isResendCoolingDown,
        start: startResendCooldown,
        reset: resetResendCooldown,
    } = useOtpResendCooldown();

    const nameInputRef = useRef(null);
    const otpInputRef = useRef(null);

    useEffect(() => {
        const target = step === 'verify' ? otpInputRef : nameInputRef;
        const t = window.setTimeout(() => target.current?.focus(), 120);
        return () => window.clearTimeout(t);
    }, [step]);

    const clearError = () => {
        if (error) setError(null);
    };

    const saveProfile = async (name, region) => {
        const response = await fetch(`${authBaseUrl}/complete-profile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name, region }),
        });
        const data = await readJson(response);
        if (isSessionExpiredResponse(response, data)) throw sessionExpiredError();
        if (!response.ok) throw new Error(data?.detail || 'Unable to save your details.');
    };

    // Sends (or resends) the code to the contact being added.
    const sendContactCode = async (target) => {
        const response = await fetch(`${authBaseUrl}/${needsEmail ? 'add-email' : 'request-otp'}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(needsEmail ? { email: target } : { phone: target }),
        });
        const data = await readJson(response);
        if (isSessionExpiredResponse(response, data)) throw sessionExpiredError();
        if (!response.ok) {
            throw new Error(
                needsEmail
                    ? getEmailAuthErrorMessage(response, data, 'Unable to send the code.')
                    : (data?.detail || data?.error || 'Unable to send the code.')
            );
        }
    };

    const handleFailure = (err, fallback) => {
        if (err?.sessionExpired) {
            if (onSessionExpired) onSessionExpired();
            return;
        }
        setError(err?.message || fallback);
    };

    const handleContinue = async () => {
        if (busy) return;
        const name = normalizeProfileName(nameValue);
        // Collected as a ZIP code; stored in users.region.
        const region = normalizeZipCode(regionValue);

        const nameError = getProfileNameError(name);
        if (nameError) {
            setError(nameError);
            return;
        }

        let contactTarget = '';
        if (needsEmail) {
            const emailError = getEmailValidationError(emailValue);
            if (emailError) {
                setError(emailError);
                return;
            }
            contactTarget = normalizeEmail(emailValue);
        } else if (needsPhone) {
            const phoneError = getPhoneValidationError(phoneValue, phoneCountry);
            if (phoneError) {
                setError(phoneError);
                return;
            }
            contactTarget = normalizePhoneForOtp(phoneValue, phoneCountry);
        }

        const zipError = getZipCodeError(regionValue);
        if (zipError) {
            setError(zipError);
            return;
        }

        setBusy(true);
        setError(null);
        setNotice(null);

        try {
            // Saved first so the name and region are kept even if the user stops at
            // the code step.
            await saveProfile(name, region);

            if (!contactTarget) {
                await onComplete();
                return;
            }

            await sendContactCode(contactTarget);
            setPendingContact(contactTarget);
            setOtpValue('');
            setMergePreview(null);
            setStep('verify');
            startResendCooldown();
        } catch (err) {
            handleFailure(err, 'Unable to save your details.');
        } finally {
            setBusy(false);
        }
    };

    const handleResend = async () => {
        if (busy || isResendCoolingDown) return;
        setBusy(true);
        setError(null);
        try {
            await sendContactCode(pendingContact);
            setNotice('New one time code sent to');
            startResendCooldown();
        } catch (err) {
            handleFailure(err, 'Unable to send the code.');
        } finally {
            setBusy(false);
        }
    };

    // confirmMerge=true replays the same verified code with the user's consent to
    // combine the two accounts, after the server has said what would move.
    const handleVerify = async (confirmMerge = false) => {
        if (busy) return;
        const otp = otpValue.replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (otp.length !== OTP_LENGTH) {
            setError(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }

        setBusy(true);
        setError(null);

        try {
            const response = await fetch(`${authBaseUrl}/${needsEmail ? 'verify-added-email' : 'verify-added-phone'}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(
                    needsEmail
                        ? { email: pendingContact, otp, confirm_merge: confirmMerge }
                        : { phone: pendingContact, otp, confirm_merge: confirmMerge }
                ),
            });
            const data = await readJson(response);
            if (response.status === 409 && data?.merge_available && !confirmMerge) {
                setMergePreview(data.merge_preview || {});
                return;
            }
            if (isSessionExpiredResponse(response, data)) throw sessionExpiredError();
            if (!response.ok) {
                throw new Error(
                    needsEmail
                        ? getEmailAuthErrorMessage(response, data, 'Invalid or expired code.')
                        : (data?.detail || data?.error || 'Invalid or expired code.')
                );
            }

            // A merge can leave the session on the other account's row, so the name
            // and region are written again to whichever row the session now points at.
            if (data?.merged) {
                await saveProfile(
                    nameValue.trim().replace(/\s+/g, ' '),
                    normalizeZipCode(regionValue),
                );
            }
            await onComplete();
        } catch (err) {
            handleFailure(err, 'Invalid or expired code.');
        } finally {
            setBusy(false);
        }
    };

    const backToDetails = () => {
        setStep('details');
        setOtpValue('');
        setMergePreview(null);
        setError(null);
        setNotice(null);
        resetResendCooldown();
    };

    const handleEnter = (action) => (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        action();
    };

    const isVerifyStep = step === 'verify';
    const pendingLabel = needsEmail ? pendingContact : formatPhoneLabel(pendingContact);
    const resendLabel = resendSeconds > 0 ? `Resend Code in ${resendSeconds}s` : 'Resend Code';
    const eyebrow = isVerifyStep ? 'Verification' : 'Welcome to CarFido';
    const title = isVerifyStep
        ? (needsEmail ? 'Verify your email' : 'Verify your phone')
        : 'Tell us about you';
    const description = isVerifyStep ? (
        <>
            {notice || 'One time code sent to'}{' '}
            <span className="ml-1 break-all font-black text-fg">{pendingLabel}</span>.
        </>
    ) : 'You\'re signed in. Finish your driver profile so we can keep your account details in one place.';

    return (
        <div className="animate-dash-overlay-in fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/50 px-3 py-4 backdrop-blur-sm sm:px-4">
            <style>{PHONE_INPUT_STYLES}{KIRUSA_PHONE_STYLES}{`.carfido-profile-phone .form-control { height: 48px !important; }`}</style>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="profile-setup-title"
                className="animate-dash-modal-in relative my-auto w-full max-w-[460px] rounded-[24px] bg-surface px-5 py-6 font-brand text-fg shadow-[0_30px_80px_rgba(0,0,0,0.25)] sm:px-8 md:rounded-[28px]"
            >
                <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[32px] overflow-hidden rounded-t-[28px] sm:rounded-t-[32px]" style={{ background: 'linear-gradient(to right, #414141, #D81F26)' }}>
                    <div className="absolute inset-x-0 bottom-0 top-[6px] bg-white"></div>
                </div>
                <div className="relative z-10 mb-5 md:mb-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#D81F26] sm:text-[11px]">{eyebrow}</p>
                    <h2 id="profile-setup-title" className="mt-1.5 text-[22px] font-black leading-tight tracking-tight text-black sm:mt-2 sm:text-2xl">
                        {title}
                    </h2>
                    <p className="mt-1.5 text-[14px] font-medium leading-relaxed text-fg-muted">
                        {description}
                    </p>
                </div>

                {!isVerifyStep ? (
                    <div className="space-y-2.5">
                        <div>
                            <label htmlFor="profile-setup-name" className={labelClass}>Full Name</label>
                            <input
                                id="profile-setup-name"
                                ref={nameInputRef}
                                type="text"
                                autoComplete="name"
                                maxLength={PROFILE_FIELD_MAX_LENGTH}
                                value={nameValue}
                                aria-invalid={error === NAME_INVALID_MESSAGE}
                                aria-describedby={error === NAME_INVALID_MESSAGE ? 'profile-setup-name-error' : undefined}
                                onChange={(e) => { setNameValue(e.target.value); clearError(); }}
                                onKeyDown={handleEnter(handleContinue)}
                                placeholder="Your full name"
                                className={inputClass}
                            />
                            {error === NAME_INVALID_MESSAGE && <p id="profile-setup-name-error" role="alert" className="mt-2 text-sm font-bold text-danger">{error}</p>}
                        </div>

                        {needsPhone ? (
                            <div>
                                <label htmlFor="profile-setup-phone" className={labelClass}>Phone Number</label>
                                <PhoneInput
                                    country={'us'}
                                    value={phoneValue}
                                    onChange={(value, country) => {
                                        setPhoneValue(String(value || '').replace(/\D/g, ''));
                                        if (country?.dialCode) setPhoneCountry(country);
                                        clearError();
                                    }}
                                    placeholder="908 242 3514"
                                    enableSearch={true}
                                    searchPlaceholder="Search country or code…"
                                    disableSearchIcon={true}
                                    disableCountryGuess={true}
                                    disableInitialCountryGuess={false}
                                    countryCodeEditable={false}
                                    containerClass="cardash-phone-container carfido-login-phone carfido-profile-phone"
                                    dropdownClass="cardash-phone-dropdown carfido-login-phone-dropdown"
                                    inputProps={{
                                        id: 'profile-setup-phone',
                                        autoComplete: 'tel',
                                        onKeyDown: handleEnter(handleContinue),
                                    }}
                                />
                            </div>
                        ) : (
                            <VerifiedContactField id="profile-setup-phone" label="Phone Number" value={formatPhoneLabel(signedInPhone)} />
                        )}

                        {needsEmail ? (
                            <div>
                                <label htmlFor="profile-setup-email" className={labelClass}>Email Address</label>
                                <input
                                    id="profile-setup-email"
                                    type="email"
                                    inputMode="email"
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    maxLength={254}
                                    value={emailValue}
                                    onChange={(e) => { setEmailValue(e.target.value); clearError(); }}
                                    onKeyDown={handleEnter(handleContinue)}
                                    placeholder="you@example.com"
                                    className={inputClass}
                                />
                            </div>
                        ) : (
                            <VerifiedContactField id="profile-setup-email" label="Email Address" value={signedInEmail} />
                        )}

                        <div>
                            <label htmlFor="profile-setup-region" className={labelClass}>ZIP Code</label>
                            <input
                                id="profile-setup-region"
                                type="text"
                                inputMode="numeric"
                                autoComplete="postal-code"
                                value={regionValue}
                                onChange={(e) => { setRegionValue(e.target.value); clearError(); }}
                                onKeyDown={handleEnter(handleContinue)}
                                placeholder="e.g. 07030"
                                className={inputClass}
                            />
                            <p className="mt-2 text-[12px] font-semibold text-fg-muted">
                                Your ZIP code helps us find repair shops near you.
                            </p>
                        </div>

                        {(needsEmail || needsPhone) && (
                            <p className="text-[12px] font-semibold leading-relaxed text-fg-muted">
                                We'll send a one-time code to verify your {contactWhat} before linking it.
                            </p>
                        )}

                        {error && error !== NAME_INVALID_MESSAGE && <div className={errorBoxClass}>{error}</div>}

                        <button type="button" onClick={handleContinue} disabled={busy} className={primaryButtonClass}>
                            {busy ? 'Saving...' : 'Continue'}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        <input
                            ref={otpInputRef}
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]{6}"
                            maxLength={OTP_LENGTH}
                            value={otpValue}
                            onChange={(e) => { setOtpValue(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH)); clearError(); }}
                            onKeyDown={handleEnter(() => handleVerify())}
                            disabled={Boolean(mergePreview)}
                            placeholder="Enter 6-digit code"
                            aria-label="Verification code"
                            className="w-full rounded-2xl border border-black/10 bg-neutral-50 px-4 py-4 text-base font-bold tracking-[0.25em] text-black outline-none transition focus:border-[#D81F26] focus:bg-white disabled:opacity-60"
                        />

                        {mergePreview && (
                            <MergePrompt
                                preview={mergePreview}
                                what={needsEmail ? 'email' : 'phone number'}
                                busy={busy}
                                onConfirm={() => handleVerify(true)}
                                onCancel={backToDetails}
                            />
                        )}

                        {error && <div className={errorBoxClass}>{error}</div>}

                        {!mergePreview && (
                            <>
                                <div className="flex justify-end pr-2">
                                    <button
                                        type="button"
                                        onClick={handleResend}
                                        disabled={busy || isResendCoolingDown}
                                        aria-label={resendLabel}
                                        className="text-[11px] font-black uppercase tracking-[0.16em] text-[#D81F26] transition hover:text-black disabled:opacity-50"
                                    >
                                        {resendLabel}
                                    </button>
                                </div>

                                <div className="flex gap-2.5 sm:gap-3">
                                    <button
                                        type="button"
                                        onClick={backToDetails}
                                        disabled={busy}
                                        className={`min-h-[48px] flex-1 rounded-full border border-border-control bg-surface px-3 text-sm font-bold text-fg transition disabled:opacity-60 ${focusClass}`}
                                    >
                                        {needsEmail ? 'Change Email' : 'Change Number'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleVerify()}
                                        disabled={busy}
                                        className={`${primaryButtonClass} flex-1 !w-auto px-3 text-sm`}
                                    >
                                        {busy ? 'Verifying...' : 'Verify Code'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* The step is mandatory, so this is the only way out of it. */}
                <div className="mt-4 border-t border-fg-muted/20 pt-3 text-center">
                    <p className="text-[12px] font-bold text-fg-muted">
                        Not you?{' '}
                        <button
                            type="button"
                            onClick={onLogout}
                            disabled={busy}
                            className="text-[11px] font-black uppercase tracking-[0.16em] text-[#D81F26] transition hover:text-black disabled:opacity-50"
                        >
                            Log out
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ProfileSetupModal;
